import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  Typography,
  Avatar,
  Skeleton,
  Chip
} from '@mui/material';
import {
  LocalShipping,
  SwapHoriz,
  Inventory2,
  Person
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useRecentActivity } from '../hooks/useDashboardData';

const getOperationIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'shipment':
      return <LocalShipping />;
    case 'reallocation':
      return <SwapHoriz />;
    case 'receipt':
      return <Inventory2 />;
    default:
      return <Inventory2 />;
  }
};

const getOperationColor = (type: string, theme: any) => {
  switch (type.toLowerCase()) {
    case 'shipment':
      return theme.palette.info.main;
    case 'reallocation':
      return theme.palette.warning.main;
    case 'receipt':
      return theme.palette.success.main;
    default:
      return theme.palette.primary.main;
  }
};

export const RecentActivityTimeline: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useRecentActivity(10);

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Typography variant="h4" fontWeight={700}>
            Recent Activity
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Array.from(new Array(5)).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="circular" width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </Box>
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Typography color="error">Failed to load activities</Typography>
        ) : data && data.activities.length > 0 ? (
          <Box
            sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '24px',
                top: '48px',
                bottom: '24px',
                width: '2px',
                backgroundColor: theme.palette.divider
              }
            }}
          >
            {data.activities.map((activity, index) => (
              <Box
                key={activity.operationId}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: index < data.activities.length - 1 ? 4 : 0,
                  position: 'relative'
                }}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: getOperationColor(activity.type, theme),
                    zIndex: 1
                  }}
                >
                  {getOperationIcon(activity.type)}
                </Avatar>
                <Box sx={{ flex: 1, pt: 0.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 0.5
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {activity.type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true
                      })}
                    </Typography>
                  </Box>
                  {activity.refNumber && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Ref: {activity.refNumber}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={<Person sx={{ fontSize: '1rem' }} />}
                      label={activity.userName}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip
                      label={`${activity.totalQuantity} units`}
                      size="small"
                      sx={{
                        backgroundColor: theme.colors.alpha.black[5],
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    />
                    <Chip
                      label={`${activity.recordCount} items`}
                      size="small"
                      sx={{
                        backgroundColor: theme.colors.alpha.black[5],
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">No recent activity</Typography>
        )}
      </CardContent>
    </Card>
  );
};